'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import type { TourStep } from '../types';

/**
 * Read the current browser location as a comparable key.
 * Returns e.g. "/settings?section=agency" or "/contacts".
 */
function getCurrentLocationKey(): string {
  const { pathname, search } = window.location;
  return search ? `${pathname}${search}` : pathname;
}

/**
 * Shared auto-advance hook for tour overlays.
 *
 * Handles four polling patterns:
 * 1. `navigateTo` — auto-navigate when the step becomes active
 * 2. `advanceOnNavigate` — poll URL and advance when user navigates
 * 3. `advanceOnElement` — poll DOM and advance when element appears
 * 4. `advanceOnElementRemoved` — poll DOM and advance when element disappears
 */
export function useTourAutoAdvance(
  isActive: boolean,
  currentStep: TourStep | null,
  nextStep: () => void,
) {
  const router = useRouter();
  const advancedRef = useRef(false);
  const navigatedRef = useRef<string | null>(null);

  // Reset the "already advanced" guard when the step changes
  useEffect(() => {
    advancedRef.current = false;
  }, [currentStep?.id]);

  // Auto-navigate when a step has navigateTo
  useEffect(() => {
    if (!isActive || !currentStep?.navigateTo) return;
    if (navigatedRef.current === currentStep.id) return;
    navigatedRef.current = currentStep.id;
    router.push(currentStep.navigateTo);
  }, [isActive, currentStep?.id, currentStep?.navigateTo, router]);

  // Poll the browser URL for advanceOnNavigate
  useEffect(() => {
    if (!isActive || !currentStep?.advanceOnNavigate) return;

    const target = currentStep.advanceOnNavigate;
    const initialLocation = getCurrentLocationKey();

    const interval = setInterval(() => {
      if (advancedRef.current) return;

      const current = getCurrentLocationKey();
      if (current === initialLocation) return;

      const pathname = window.location.pathname;
      if (current === target || pathname === target) {
        advancedRef.current = true;
        nextStep();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [isActive, currentStep?.id, currentStep?.advanceOnNavigate, nextStep]);

  // Poll the DOM for advanceOnElement
  useEffect(() => {
    if (!isActive || !currentStep?.advanceOnElement) return;

    const selector = currentStep.advanceOnElement;

    const interval = setInterval(() => {
      if (advancedRef.current) return;

      const el = document.querySelector(selector);
      if (el) {
        advancedRef.current = true;
        nextStep();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [isActive, currentStep?.id, currentStep?.advanceOnElement, nextStep]);

  // Poll the DOM for advanceOnElementRemoved
  useEffect(() => {
    if (!isActive || !currentStep?.advanceOnElementRemoved) return;

    const selector = currentStep.advanceOnElementRemoved;
    let elementWasPresent = !!document.querySelector(selector);

    const interval = setInterval(() => {
      if (advancedRef.current) return;

      const el = document.querySelector(selector);
      if (el) {
        elementWasPresent = true;
      } else if (elementWasPresent) {
        advancedRef.current = true;
        nextStep();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [isActive, currentStep?.id, currentStep?.advanceOnElementRemoved, nextStep]);
}
